import React, { useRef, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Formik } from "formik";
import { ModalBody, ModalFooter } from "application/modal/index";
import * as classNames from "classnames";
import CheckBox from "modules/admin/adminIdeas/addForm/checkBox";
import { projectStagesTemplatesGet } from "modules/projects/newProject/stepOne/stepOne.actions";

import { useModelPopup } from "../../../../common/hooks";
import {
  Label,
  TextBox,
  Button,
  BtnType,
  DropSelect,
} from "../../../primitives";
import { model, validation } from "./newProjectModal.constant";
import styles from "./styles.module.scss";
import CompaniesItem from "./companies/companiesBlock";
import MembersItem from "./members/membersBlock";
import {
  themesProjectCreateAttempt,
  themesCompanyGetAttempt,
} from "../../themesIndividualPage/themesOverviewTab/overviewTab.action";
import { Loading } from "modules/primitives/index";

const NewProjectModal = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [initModel, setInitModel] = useState(model);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAllMembers, setSelectAllMembers] = useState(true);
  const [selectAllCompanies, setSelectAllCompanies] = useState(true);

  const history = useHistory();
  const popup = useModelPopup();
  const formRef = useRef();
  const dispatch = useDispatch();

  const {
    councilReducer: { selectedCouncil },
    stepOneReducer: { stageTemplates },
    themesReducer: {
      themesMainReducer: { currentThemeData },
      themesOverviewReducer: { companyList, loadingCompany },
    },
  } = useSelector((state) => state);

  const optionVisibility = [
    {
      text: "All (visible to everyone)",
      value: "all",
    },
    {
      text: "Project Members",
      value: "project_members",
    },
  ];

  const selectAllCompaniesCallback = useCallback(() => {
    const newValue = !selectAllCompanies;
    setSelectAllCompanies(newValue);

    if (!newValue) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(currentThemeData?.companies.map((company) => company.company_id));
    }
  }, [currentThemeData, selectAllCompanies, setSelectAllCompanies]);

  const selectAllMembersCallback = useCallback(() => {
    const newValue = !selectAllMembers;
    setSelectAllMembers(newValue);

    if (!newValue) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(currentThemeData?.members.map((member) => member.user_id));
    }
  }, [currentThemeData, selectAllMembers, setSelectAllMembers]);

  const storeCompanies = (companyId) => {
    if (selectedCompanies.includes(companyId)) {
      const filteredArray = selectedCompanies
        .filter((selectedCompanyId) => selectedCompanyId !== companyId);

      setSelectedCompanies([...filteredArray]);

      if (filteredArray.length !== currentThemeData?.companies.length) {
        setSelectAllCompanies(false);
      }
    } else {
      const findCompany = currentThemeData?.companies
        .find((company) => company.company_id === companyId);
      const ids = [...selectedCompanies, findCompany.company_id];

      if (ids.length === currentThemeData?.companies.length) {
        setSelectAllCompanies(true);
      }

      setSelectedCompanies([...selectedCompanies, findCompany.company_id]);
    }
  };

  const storeMembers = (memberId) => {
    if (selectedMembers.includes(memberId)) {
      const filteredArray = selectedMembers
        .filter((selectedMemberId) => selectedMemberId !== memberId);

      setSelectedMembers([...filteredArray]);

      if (filteredArray.length !== currentThemeData?.members.length) {
        setSelectAllMembers(false);
      }
    } else {
      const findMember = currentThemeData?.members.find((member) => member.user_id === memberId);
      const ids = [...selectedMembers, findMember.user_id];

      if (ids.length === currentThemeData?.members.length) {
        setSelectAllMembers(true);
      }

      setSelectedMembers([...selectedMembers, findMember.user_id]);
    }
  };

  const evaluateTemplateOptions = () => {
    if (stageTemplates) {
      return stageTemplates.filter((template) => template.patient_organization === false);
    }
  };

  const handleDismiss = () => {
    popup.hide();
  };

  const handleProjectRedirect = (id) => {
    history.push(`/projects/${id}/overview`);
  };

  const handleCreate = () => {
    let member_ids_on_submit;
    let company_ids_on_submit;

    if (selectAllMembers) {
      member_ids_on_submit = [];
    } else {
      member_ids_on_submit = selectedMembers;
    }
    if (selectAllCompanies) {
      company_ids_on_submit = [];
    } else {
      company_ids_on_submit = selectedCompanies;
    }
    const data = {
      ...formRef?.current?.values,
      company_ids: company_ids_on_submit,
      member_ids: member_ids_on_submit,
      stage_template_id: formRef?.current?.values?.stage_template_id,
      add_all_theme_companies: selectAllCompanies,
      add_all_theme_members: selectAllMembers,
    };
    dispatch(
      themesProjectCreateAttempt({
        themeId: currentThemeData?.id,
        data,
        enqueueSnackbar,
        handleProjectRedirect,
      })
    );
    popup.hide();
  };

  useEffect(() => {
    if (selectAllCompanies) {
      const companiesIds = currentThemeData?.companies.map((company) => company.company_id);
      setSelectedCompanies(companiesIds);
    }
    if (selectAllMembers) {
      const membersIds = currentThemeData?.members.map((member) => member.user_id);
      setSelectedMembers(membersIds);
    }
  }, [selectAllCompanies, selectAllMembers]);

  useEffect(() => {
    localStorage.removeItem("new_project_object");
    dispatch(
      projectStagesTemplatesGet({
        council_id: selectedCouncil.id,
        enqueueSnackbar,
      })
    );
  }, []);

  useEffect(() => {
    if (currentThemeData.name && currentThemeData.id) {
      dispatch(
        themesCompanyGetAttempt({
          id: currentThemeData.id,
          enqueueSnackbar,
        })
      );

      setInitModel((prev) => {
        return { ...prev, name: currentThemeData.name };
      });
    }
  }, [currentThemeData]);

  useEffect(() => {
    if (
      stageTemplates?.length &&
      evaluateTemplateOptions()[0] &&
      evaluateTemplateOptions().length > 0
    ) {
      setInitModel({
        ...formRef.current?.values,
        name: currentThemeData.name,
        stage_template_id: evaluateTemplateOptions().find((stage) => stage.default_template).id,
      });
    }
  }, [stageTemplates]);

  return (
    <div>
      <ModalBody>
        <div className={styles.inner}>
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initModel && initModel}
            validationSchema={validation && validation}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              setSubmitting(true);
              //   onSubmitValue(values);
              resetForm();
            }}
          >
            {({ handleSubmit, setFieldValue, ...formikprops }) => (
              <form onSubmit={handleSubmit}>
                <div className={styles.formElement}>
                  <Label>Project name</Label>
                  <TextBox
                    type="text"
                    name="name"
                    placeholder="Project name"
                    formProps={formikprops}
                    className={styles.textBox}
                  />
                </div>
                <div className={styles.formElement}>
                  <Label>Access</Label>
                  <DropSelect
                    onChange={(e) => {
                      setFieldValue("visibility", e.val);
                    }}
                    placeholder="Access"
                    name="visibility"
                    labelField="text"
                    valueField="value"
                    searchBy="text"
                    options={optionVisibility}
                    formProps={formikprops}
                  />
                </div>

                <div className={styles.formElement}>
                  <Label>Evaluation Template</Label>
                  <DropSelect
                    onChange={(e) => setFieldValue("stage_template_id", e.val)}
                    placeholder="Evaluation Template"
                    name="stage_template_id"
                    labelField="name"
                    valueField="id"
                    searchBy="name"
                    options={evaluateTemplateOptions()}
                    formProps={formikprops}
                  />
                  <p className="caption small">
                    Select your Team Evaluation Template to set up the stages and tasks for your
                    project
                  </p>
                </div>
                <Label>Select company</Label>
                <div className={styles.selectAllCompanies}>
                  <Label>Select all companies</Label>
                  <CheckBox
                    onChange={selectAllCompaniesCallback}
                    className={styles.selectCompCheckbox}
                    isChecked={selectAllCompanies}
                  />
                </div>
                <div className={styles.companiesBlockContainer}>
                  {loadingCompany ? (
                    <Loading />
                  ) : companyList.length > 0 ? (
                    companyList.filter(c => !c.is_product).map((company) => (
                      <div key={`${company.company_id}`} className={classNames(styles.Row)}>
                        <CompaniesItem
                          storeCompanies={storeCompanies}
                          company={company}
                          selectAllCompanies={selectAllCompanies}
                          selected={selectedCompanies.includes(company.company_id)}
                        />
                      </div>
                    ))
                  ) : (
                    <span className="p-3 text-center">No companies yet!</span>
                  )}
                </div>
                <Label>Select member</Label>
                <div className={styles.selectAllCompanies}>
                  <Label>Select all members</Label>
                  <CheckBox
                    onChange={selectAllMembersCallback}
                    className={styles.selectMemberCheckbox}
                    isChecked={selectAllMembers}
                  />
                </div>
                <div className={styles.membersBlockContainer}>
                  {currentThemeData.members?.length > 0 ? (
                    currentThemeData.members.map((member) => (
                      <div
                        key={`${member.user_id}`}
                        className={classNames(styles.Row, styles.companiesBlockItem)}
                      >
                        <MembersItem
                          storeMembers={storeMembers}
                          member={member}
                          selectAllMembers={selectAllMembers}
                          selected={selectedMembers.includes(member.user_id)}
                        />
                      </div>
                    ))
                  ) : (
                    <span className="p-3 text-center">No members yet!</span>
                  )}
                </div>
              </form>
            )}
          </Formik>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button className={styles.cancelButton} btn={BtnType.OUTLINE} onClick={handleDismiss}>
          Cancel
        </Button>
        <Button onClick={() => handleCreate()} btn={BtnType.HIGHLIGHT} className={styles.button}>
          Create
        </Button>
      </ModalFooter>
    </div>
  );
};

export default NewProjectModal;
